import { useCallback, useEffect, useRef, useState } from 'react';
import type { ResellerAiUsageReport } from '@/api/generated';
import { ApiError } from '@/api/errors';
import { useTenantApi } from '@/tenant/useTenant';

export interface AiUsageState {
  status: 'loading' | 'ready' | 'error';
  data: ResellerAiUsageReport | null;
  error: ApiError | null;
  reload: () => void;
}

function toApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError('UNKNOWN', 0, {});
}

/** Loads AI usage for a civil-date period. Refetches when from/to change. */
export function useAiUsage(params?: { from?: string; to?: string }): AiUsageState {
  const [status, setStatus] = useState<AiUsageState['status']>('loading');
  const [data, setData] = useState<ResellerAiUsageReport | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const api = useTenantApi();
  const started = useRef(false);
  const from = params?.from;
  const to = params?.to;
  const rangeKey = `${from ?? ''}|${to ?? ''}`;
  const rangeKeyRef = useRef(rangeKey);
  rangeKeyRef.current = rangeKey;

  const reload = useCallback(() => {
    const key = `${from ?? ''}|${to ?? ''}`;
    setStatus('loading');
    setError(null);
    const query: { from?: string; to?: string } = {};
    if (from !== undefined) query.from = from;
    if (to !== undefined) query.to = to;
    api.getAiUsage(Object.keys(query).length > 0 ? query : undefined).then(
      (next) => {
        if (rangeKeyRef.current !== key) return;
        setData(next);
        setError(null);
        setStatus('ready');
      },
      (err: unknown) => {
        if (rangeKeyRef.current !== key) return;
        setData(null);
        setError(toApiError(err));
        setStatus('error');
      },
    );
  }, [api, from, to]);

  useEffect(() => {
    if (!started.current) {
      started.current = true;
      reload();
      return;
    }
    reload();
  }, [reload]);

  return { status, data, error, reload };
}
